import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Switch } from '@/shared/ui/Switch'
import { Button } from '@/shared/ui/Button'

const inputClass = cn(
  'w-full rounded-md border border-border bg-input px-3 py-2 text-sm text-foreground',
  'placeholder:text-muted-foreground',
  'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
  'disabled:opacity-50 disabled:cursor-not-allowed',
)

function ComingSoonBadge({ label }: { label: string }) {
  return (
    <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
      {label}
    </span>
  )
}

function SectionCard({
  title,
  comingSoon,
  children,
}: {
  title: string
  comingSoon: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <ComingSoonBadge label={comingSoon} />
      </div>
      <div className="space-y-4 px-6 py-6">{children}</div>
    </div>
  )
}

export function SettingsNotificationsTab() {
  const { t } = useTranslation('settings')

  const [emailEnabled, setEmailEnabled] = useState(false)
  const [emailRecipient, setEmailRecipient] = useState('')
  const [webhookEnabled, setWebhookEnabled] = useState(false)
  const [webhookUrl, setWebhookUrl] = useState('')
  const [spikeThreshold, setSpikeThreshold] = useState(50)

  const comingSoon = t('notifications.comingSoon')

  return (
    <div className="space-y-4">
      <SectionCard title={t('notifications.email.title')} comingSoon={comingSoon}>
        <div className="space-y-1.5">
          <label
            className="block text-sm font-medium text-foreground"
            htmlFor="notif-email-recipient"
          >
            {t('notifications.email.recipient')}
          </label>
          <input
            id="notif-email-recipient"
            type="email"
            className={inputClass}
            value={emailRecipient}
            onChange={(e) => setEmailRecipient(e.target.value)}
            disabled
          />
        </div>

        <div className="flex items-center gap-4 rounded-md border border-border bg-muted/30 px-4 py-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">
              {t('notifications.email.enabled')}
            </p>
          </div>
          <Switch
            id="notif-email-enabled"
            checked={emailEnabled}
            onChange={setEmailEnabled}
            disabled
          />
        </div>
      </SectionCard>

      <SectionCard title={t('notifications.webhook.title')} comingSoon={comingSoon}>
        <div className="space-y-1.5">
          <label
            className="block text-sm font-medium text-foreground"
            htmlFor="notif-webhook-url"
          >
            {t('notifications.webhook.url')}
          </label>
          <input
            id="notif-webhook-url"
            type="url"
            className={inputClass}
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            disabled
          />
        </div>

        <div className="flex items-center gap-4 rounded-md border border-border bg-muted/30 px-4 py-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">
              {t('notifications.webhook.enabled')}
            </p>
          </div>
          <Switch
            id="notif-webhook-enabled"
            checked={webhookEnabled}
            onChange={setWebhookEnabled}
            disabled
          />
        </div>
      </SectionCard>

      <SectionCard title={t('notifications.spike.title')} comingSoon={comingSoon}>
        <div className="space-y-1.5">
          <label
            className="block text-sm font-medium text-foreground"
            htmlFor="notif-spike-threshold"
          >
            {t('notifications.spike.threshold')}
          </label>
          <p className="text-xs text-muted-foreground">{t('notifications.spike.hint')}</p>
          <input
            id="notif-spike-threshold"
            type="number"
            min={1}
            max={1000}
            className={inputClass}
            value={spikeThreshold}
            onChange={(e) => setSpikeThreshold(Number(e.target.value))}
            disabled
          />
        </div>
      </SectionCard>

      <div className="flex">
        <Button type="button" variant="primary" disabled>
          {t('notifications.save')}
        </Button>
      </div>
    </div>
  )
}
